import { useState } from 'react'

import { Button, CheckboxField, SelectField, StatusMessage } from '@krp/ui-kit'

export interface PrintSettingsScreenProps {
  deviceId: number
  count: number | null
  /** Оплата по QR-коду СБП на терминале; `null` — ещё не прочитано. */
  acceptQr: boolean | null
  /** «Быстрая оплата» кассы; `null` — ещё не прочитано. */
  fastPayment: boolean | null
  busy: boolean
  canSave: boolean
  error: { message: string; meta: string } | null
  saved: boolean
  onSave: (count: number, acceptQr: boolean, fastPayment: boolean) => void
  onReload: () => void
}

/**
 * Настройки РМК этой кассы (решение владельца 23.10.2026): «быстрая оплата» (24.10.2026), оплата по QR-коду СБП на терминале (по умолчанию включена, как в 1С) и копии
 * банковского слипа. Состав опций принадлежит конкретному кассовому ядру.
 */
export function PrintSettingsScreen(props: PrintSettingsScreenProps) {
  const [draft, setDraft] = useState(String(props.count ?? 0))
  const [qr, setQr] = useState(props.acceptQr ?? true)
  const [fast, setFast] = useState(props.fastPayment ?? false)
  const changed = draft !== String(props.count) || qr !== props.acceptQr || fast !== props.fastPayment
  return (
    <section className="rmk-shift-panel" aria-label="Настройки РМК">
      <h2>Настройки РМК</h2>
      <p>Касса AQSI №{props.deviceId}</p>
      {props.count === null && !props.error && <StatusMessage kind="loading" title="Читаем настройки РМК…" />}
      {props.error && <StatusMessage kind="error" title="Настройки РМК" text={props.error.message} meta={props.error.meta} />}
      {props.acceptQr !== null && <CheckboxField
        label="Принимать оплату по QR-коду (СБП) на терминале"
        checked={qr}
        disabled={props.busy || !props.canSave}
        onChange={setQr}
      />}
      <p>Включено — на терминале при оплате «Картой» можно приложить карту или оплатить по QR-коду СБП. Выключено — только карта.</p>
      {props.fastPayment !== null && <CheckboxField
        label="Быстрая оплата: после оплаты чек печатается фоном, а кассир сразу набирает следующую продажу"
        checked={fast}
        disabled={props.busy || !props.canSave}
        onChange={setFast}
      />}
      <p>Чек на бумаге печатается всегда. Ход чека виден справа на экране продажи; новая оплата дождётся конца печати предыдущего чека, а если с чеком
        проблема — оплата не начнётся, пока вы её не решите. Выключено — окно оплаты ведёт до слов «Чек пробит».</p>
      <p>Количество банковских слипов при оплате и возврате картой. Фискальный чек печатается отдельно, по своим настройкам.</p>
      {props.count !== null && <SelectField
        label="Количество слипов эквайринга"
        value={draft}
        options={[{ value: '0', label: '0 — не печатать (по умолчанию)' }, { value: '1', label: '1 копия' }, { value: '2', label: '2 копии' }]}
        disabled={props.busy || !props.canSave}
        onChange={setDraft}
      />}
      <p>Сохраняется только для этой кассы и применяется к следующим оплатам и возвратам. Уже отправленные операции не меняются.</p>
      {!props.canSave && <p>Для изменения начните работу за этой кассой.</p>}
      <div className="aqsi-card__actions">
        <Button disabled={props.busy || !props.canSave || props.count === null || props.error !== null} onClick={() => props.onSave(Number(draft), qr, fast)}>
          {props.busy ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        <Button variant="ghost" disabled={props.busy} onClick={props.onReload}>Обновить</Button>
      </div>
      {props.saved && !changed && <p role="status">Настройки сохранены для этой кассы.</p>}
    </section>
  )
}
